// ============================================================================
// useCan — Canonical permission-check hook for screens
// ============================================================================
// Single entry point that every scaffolded screen uses to gate UI:
//   const can = useCan('sales-order-master');
//   if (can('CREATE')) { ... }
//   { ...actionButtonFactory.add(handleAdd), visible: can('CREATE') }
//
// The bypass via REACT_APP_IS_DEV_DEMO=true is the canonical "show every
// button while the backend permission catalog is still under construction"
// pattern from SampleMasterScreen. Default is false (real permissions enforced).
//
// IMPORTANT — for production tenant deployments, leave the env var UNSET. The
// dev-demo bypass is for scaffold bring-up only; it lets new screens render
// every action before permission rows have been seeded server-side.

import { useMemo } from 'react';
import { usePermissions } from 'hooks/usePermissions';
import type { ScreenCode } from 'auth/screenCodes';

// IMPORTANT: must use direct member-access `process.env.X` — NOT optional
// chaining `process.env?.X`. CRA's webpack DefinePlugin pattern-matches the
// direct form and replaces it with the literal env-var string at compile
// time. The optional-chained form is NOT recognized by DefinePlugin and
// falls through to the runtime `process.env` polyfill (an empty stub `{}`),
// so the comparison always reads `undefined` and the bypass never activates.
//
// CRA's runtime polyfill ensures `process.env` always exists, so the
// typeof-guard from older versions of this file is unnecessary.
// eslint-disable-next-line no-undef
const IS_DEV_DEMO = process.env.REACT_APP_IS_DEV_DEMO === 'true';

export type CanFn = (action: string) => boolean;

/**
 * Returns a memoised `(action) => boolean` checker bound to one resource.
 *
 * `resource` MUST be the catalog **screen code** — `app.tbl_app_ui_screen
 * .screen_code`, which in Fairbooks is kebab-case (e.g. 'client-master',
 * 'journal-entry'). `hasAction` resolves the code against the maps built from
 * `/me/permissions`, and those maps are keyed by catalog codes only. Pass a
 * code with no seeded catalog row and it matches nothing: every check returns
 * false, for every user, administrators included, with no error and no
 * warning. The IS_DEV_DEMO bypass below hides that during local development,
 * so the failure first appears in a deployed environment as "the buttons are
 * gone". The `ScreenCode` union (auth/screenCodes.ts) turns that mistake into
 * a compile error instead.
 *
 * `action` is the UPPER_SNAKE_CASE verb
 * (CREATE / UPDATE / DELETE / EXPORT / IMPORT / APPROVE / …).
 *
 * When REACT_APP_IS_DEV_DEMO=true, all checks return true — used in the
 * sample module + during scaffold bring-up before backend permissions exist.
 */
export function useCan(resource: ScreenCode): CanFn {
  const permissions = usePermissions();
  return useMemo(
    () => (action: string) => IS_DEV_DEMO || permissions.hasAction(resource, action),
    [permissions, resource],
  );
}

/** Exposed for callers that need to know whether dev-demo mode is on
 *  (e.g. to render a small banner during bring-up). */
export const isDevDemoBypass = IS_DEV_DEMO;
